import { AnyNetworkElement, isAbgangsleitung, isHausanschluss, isTrafoStation, isKabelverteilschrank } from '../types/network';
import { useNetworkElementsStore } from '../store/networkElementsStore';
import { networkApi } from '../infrastructure/api/NetworkApiClient';

interface NetworkDataResponse {
  elements: AnyNetworkElement[];
}

interface UnifiedImportResponse {
  success: boolean;
  totalImported?: number;
  layerStats?: Record<string, number>;
  warnings?: string[];
  lemonbeatWarnings?: string[];
  added?: number;
  updated?: number;
  deleted?: number;
  pendingChanges?: {
    applicable?: any[];
    conflicts?: any[];
  };
}

export class GeoImportService {

  private async loadNetworkData(): Promise<boolean> {
    const store = useNetworkElementsStore.getState();

    try {
      const data = await networkApi.getNetwork<NetworkDataResponse>();

      // Process and update all elements
      const processedElements = this.processImportedElements(data.elements || []);
      store.setElements(processedElements);
      return true;

    } catch (error) {
      console.error('[GeoImport] Error loading network data:', error);
      store.setError(true, error instanceof Error ? error.message : 'Netzwerkdaten konnten nicht geladen werden. Bitte versuchen Sie es erneut.');
      return false;
    }
  }


  private ensureRequiredFields(element: AnyNetworkElement): AnyNetworkElement {

    // Common fields
    if (!element.description) {
      element.description = element.name;
    }
    if (!element.metadata) {
      element.metadata = {};
    }
    element.switchedOn = element.switchedOn ?? true;

    if (isHausanschluss(element)) {
      const processed = {
        ...element,
        street: element.street || null,
        houseNumber: element.houseNumber || null,
        city: element.city || null,
        meLoId: element.meLoId || '',
        neLoId: element.neLoId || '',
        connectionPower: element.connectionPower || 44,
        simulatedPowerPercent: element.simulatedPowerPercent,
        loadProfile: element.loadProfile || 'H0',
        connectionKind: element.connectionKind || 'household',
        annualConsumption: element.annualConsumption || 3600,
        smgws: element.smgws || [],
        technischeRessourcen: element.technischeRessourcen || [],
        steuerboxes: element.steuerboxes || [],
        connectedLine: element.connectedLine
      };


      return processed;
    }

    if (isTrafoStation(element)) {
      return {
        ...element,
        capacity: element.capacity || 630,
        fuses: element.fuses || {}
      };
    }

    if (isKabelverteilschrank(element)) {
      return {
        ...element,
        connectedLines: element.connectedLines || []
      };
    }

    if (isAbgangsleitung(element)) {
      return {
        ...element,
        waypoints: element.waypoints || [],
        totalLength: element.totalLength || 0,
        cableType: element.cableType || 'NAYY',
        crossSection: element.crossSection || 150
      };
    }

    return element;
  }

  public processImportedElements(elements: AnyNetworkElement[]): AnyNetworkElement[] {
    return elements.map(element => this.ensureRequiredFields(element));
  }

  public async importUnifiedNetwork(file: File): Promise<void> {
    const store = useNetworkElementsStore.getState();

    try {
      store.setLoading(true);

      store.setLoadingMessage('Reading unified network file...');
      const text = await file.text();
      const data = JSON.parse(text);


      // Validate that this is a unified network format (support both old and new key names)
      const hasTrafo = !!data.Trafo;
      const hasHaus = !!(data.Haus || data.Hausanschluss);
      const hasKabelverteiler = !!(data.Kabelverteiler || data.Kvs || data.Kabelverteilschrank);
      const hasKabel = !!(data.Kabel || data.Abgang);

      if (!hasTrafo && !hasHaus && !hasKabelverteiler && !hasKabel) {
        throw new Error('Invalid unified network format. Expected keys: Trafo, Haus/Hausanschluss, Kabelverteiler/Kvs, Kabel/Abgang');
      }

      store.setLoadingMessage('Importing unified network...');


      const result = await networkApi.zonePost<UnifiedImportResponse>('/network/import/unified-network', data);


      if (!result.success) {
        console.error('[GeoImport] Server reported failure');
        throw new Error('Unified network import failed on server');
      }

      // Check for pending changes from the import
      if (result.pendingChanges) {
        const applicableChanges = result.pendingChanges.applicable ?? [];
        const conflictChanges = result.pendingChanges.conflicts ?? [];
        const hasApplicable = applicableChanges.length > 0;
        const hasConflicts = conflictChanges.length > 0;


        if (hasApplicable || hasConflicts) {
          // Store pending changes for the dialog
          store.setPendingImportChanges({
            applicable: applicableChanges,
            conflicts: conflictChanges
          });
        }
      }

    } catch (error) {
      console.error('[GeoImport] Error processing unified network:', error);
      store.setLoading(false);
      store.setError(true, error instanceof Error ? error.message : 'Unified import failed. Please try again.');
      return;
    }

    // Load updated network data
    store.setLoadingMessage('Loading updated network data...');
    try {
      const success = await this.loadNetworkData();
      if (success) {
        store.setError(false, '');
      } else {
        store.setError(true, 'Import succeeded but failed to load network data. Please refresh the page.');
      }
    } catch (loadError) {
      console.error('[GeoImport] Error loading network data:', loadError);
      store.setError(true, 'Import succeeded but failed to load network data. Please refresh the page.');
    }

    store.setLoading(false);
  }

  public async importUnifiedNetworkIncremental(file: File): Promise<void> {
    const store = useNetworkElementsStore.getState();
    let lemonbeatWarnings: string[] = [];

    try {
      store.setLoading(true);

      store.setLoadingMessage('Reading network file...');
      const text = await file.text();
      const data = JSON.parse(text);

      const hasTrafo = !!data.Trafo;
      const hasHaus = !!(data.Haus || data.Hausanschluss);
      const hasKabelverteiler = !!(data.Kabelverteiler || data.Kvs || data.Kabelverteilschrank);
      const hasKabel = !!(data.Kabel || data.Abgang);

      if (!hasTrafo && !hasHaus && !hasKabelverteiler && !hasKabel) {
        throw new Error('Invalid unified network format. Expected keys: Trafo, Haus/Hausanschluss, Kabelverteiler/Kvs, Kabel/Abgang');
      }

      store.setLoadingMessage('Importing network changes incrementally...');

      const result = await networkApi.zonePost<UnifiedImportResponse>('/network/import/unified-network-incremental', data);


      if (!result.success) {
        throw new Error('Incremental network import failed on server');
      }

      if (result.lemonbeatWarnings && result.lemonbeatWarnings.length > 0) {
        lemonbeatWarnings = result.lemonbeatWarnings;
        console.warn('[GeoImport] Lemonbeat warnings:', lemonbeatWarnings);
      }

      if (result.pendingChanges) {
        const applicableChanges = result.pendingChanges.applicable ?? [];
        const conflictChanges = result.pendingChanges.conflicts ?? [];
        if (applicableChanges.length > 0 || conflictChanges.length > 0) {
          store.setPendingImportChanges({
            applicable: applicableChanges,
            conflicts: conflictChanges
          });
        }
      }

    } catch (error) {
      console.error('[GeoImport] Error during incremental import:', error);
      store.setLoading(false);
      store.setError(true, error instanceof Error ? error.message : 'Incremental import failed. Please try again.');
      return;
    }

    store.setLoadingMessage('Loading updated network data...');
    try {
      const success = await this.loadNetworkData();
      if (success) {
        if (lemonbeatWarnings.length > 0) {
          store.setError(true, 'Warnung: ' + lemonbeatWarnings.join(' | '));
        } else {
          store.setError(false, '');
        }
      } else {
        store.setError(true, 'Import succeeded but failed to load network data. Please refresh the page.');
      }
    } catch (loadError) {
      console.error('[GeoImport] Error loading network data after incremental import:', loadError);
      store.setError(true, 'Import succeeded but failed to load network data. Please refresh the page.');
    }

    store.setLoading(false);
  }
}

export const geoImportService = new GeoImportService();
